"use client";

import * as tabler from "@tabler/icons-react";
import { IAccountAssets, IBlocks, IIcons, IUserData } from "@/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DorasIcons from "@/lib/DorasIcons";
import { generateCustomAsset, isUUID } from "@/lib/utils";
import Assets from "@/components/admin/assets";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import RenderIconOrAsset from "../../RenderIconOrAsset";
import CardSetting from "@/components/admin/settings/CardSetting";

let iconsAll: IIcons[] = [];

Object.entries(tabler).forEach(([iconName]) => {
    if (
        iconName === "createReactComponent" ||
        iconName === "iconsList" ||
        iconName === "icons"
    ) {
        return;
    }
    iconsAll.push({
        value: iconName,
        icon: (
            <RenderIconOrAsset
                account_id=""
                iconName={iconName}
                size={60}
                raw
            />
        ), // Render safely
    });
});
iconsAll = iconsAll.concat(DorasIcons);

interface Props {
    account: IUserData;
    block: IBlocks;
    update: (value: IBlocks) => void;
    assets: IAccountAssets[];
}
const checkIconInUse = (
    block: IBlocks,
    icon: string,
    assets: IAccountAssets[]
) => {
    if (block.icon === icon) return true;
    if (
        block.icon ==
        assets
            .find((e) => generateCustomAsset(e.title) == icon)
            ?.url.split("/")
            .pop()
    )
        return true;
    return false;
};
export default function IconAssetPicker({
    account,
    block,
    update,
    assets,
}: Props) {
    const [Icons, setIcons] = useState(
        iconsAll.filter((e) => e.value.includes("Brand")).slice(0, 50)
    );
    const [slectedIcon, setSelectedIcon] = useState<any>({
        icon: "",
        value: "",
    });
    useEffect(() => {
        setSelectedIcon({
            icon: (
                <RenderIconOrAsset
                    account_id={account.id}
                    iconName={block.icon}
                    size={60}
                    raw
                />
            ), // Creating React elements
            value: block.icon,
        });
    }, [block.icon, account.id]);
    return (
        <CardSetting
            noBorder
            title="Icon"
            description="Select an icon or uploaded image for your block"
        >
            <Tabs defaultValue={isUUID(block.icon) ? "assets" : "icons"}>
                <TabsList className="rounded-b-none">
                    <TabsTrigger value="icons">Icons</TabsTrigger>
                    <TabsTrigger value="assets">Assets</TabsTrigger>
                </TabsList>
                <TabsContent value="icons">
                    <div className="w-full">
                        <div className="flex items-center gap-1 rounded-t-none rounded-tr-md bg-muted pl-2">
                            <tabler.IconSearch size={18} />
                            <Input
                                type="text"
                                // label="Icon Picker"
                                variant="card"
                                className="border-b-navigationAccent placeholder:text-copy-light w-full bg-muted"
                                placeholder="Search over 5000 icons..."
                                onKeyUp={(e: any) => {
                                    const searchText =
                                        e.target.value.toLowerCase();
                                    const searchTokens = searchText
                                        .split(" ")
                                        .filter(
                                            (token: any) => token.trim() !== ""
                                        );
                                    const filteredIcons = iconsAll.filter(
                                        (icon) => {
                                            const normalizedId =
                                                icon.value.toLowerCase();
                                            return searchTokens.every(
                                                (token: any) =>
                                                    normalizedId.includes(token)
                                            );
                                        }
                                    );
                                    if (searchText.length == 0) {
                                        setIcons(
                                            iconsAll
                                                .filter((e) =>
                                                    e.value.includes("Brand")
                                                )
                                                .slice(0, 50)
                                        );
                                        return;
                                    }
                                    setIcons(filteredIcons.slice(0, 50));
                                }}
                            />
                        </div>
                        <ScrollArea className="h-72 w-full rounded-b-md border-2 border-muted bg-card px-3">
                            <div className="grid grid-cols-4 place-items-center gap-3 py-3 md:grid-cols-5">
                                {slectedIcon.icon &&
                                    slectedIcon.value.length > 0 &&
                                    !isUUID(block.icon) && (
                                        <button
                                            className={
                                                "icons-tooltip text-light-copy size-18 text-copy rounded-xl border-2 bg-primary text-3xl"
                                            }
                                            onClick={() =>
                                                update({
                                                    ...block,
                                                    icon: "",
                                                })
                                            }
                                        >
                                            {slectedIcon.icon}
                                        </button>
                                    )}
                                {Icons.map((icon, index) => {
                                    if (icon.value.startsWith("Doras")) {
                                        icon.icon = (
                                            <img
                                                className="size-[60px] shrink-0"
                                                src={icon.img}
                                                alt="icon"
                                            />
                                        );
                                    }
                                    if (
                                        checkIconInUse(
                                            block,
                                            icon.value,
                                            assets
                                        )
                                    )
                                        return;
                                    return (
                                        <button
                                            key={index}
                                            className={
                                                "icons-tooltip text-light-copy size-18 text-copy rounded-xl border-2 bg-accent text-3xl"
                                            }
                                            onClick={() => {
                                                update({
                                                    ...block,
                                                    icon: icon.value,
                                                });
                                            }}
                                        >
                                            {icon.icon}
                                        </button>
                                    );
                                })}
                            </div>
                        </ScrollArea>
                    </div>
                </TabsContent>
                <TabsContent value="assets">
                    <div className="w-full">
                        <div className="flex items-center gap-1 rounded-t-none rounded-tr-md bg-muted pl-2">
                            <tabler.IconPhoto size={18} />
                            <Input
                                type="text"
                                // label="Icon Picker"
                                variant="card"
                                className="border-b-navigationAccent placeholder:text-copy-light pointer-events-none invisible w-full bg-muted"
                                placeholder="Your assets"
                            />
                        </div>

                        <ScrollArea className="h-72 w-full rounded-b-md rounded-t-none border-2 border-muted bg-card px-3">
                            <Assets
                                sidebarPicker
                                _assets={assets || []}
                                //@ts-expect-error
                                impersonate={
                                    account.is_brand
                                        ? {
                                              id: account.id,
                                          }
                                        : null
                                }
                                //@ts-expect-error
                                block={{
                                    url: `https://cdn.doras.to/doras/assets/${account.id}/${block.icon}`,
                                }}
                                update={(asset) => {
                                    update({
                                        ...block,
                                        icon:
                                            (asset.url &&
                                                asset.url.split("/").pop()) ||
                                            "",
                                    });
                                }}
                                account_id={account.id}
                                account={account}
                            />
                        </ScrollArea>
                    </div>
                </TabsContent>
            </Tabs>
        </CardSetting>
    );
}
